/**
 * main.ts — Vite entry point for the RIGX Operador PWA.
 *
 * Boot order mirrors apps/frontend/src/main.ts: the router's first navigation
 * guard reads session claims SYNCHRONOUSLY, and the JWT lives in an httpOnly
 * cookie (not decodable by JS) — so we hydrate claims from GET /auth/session
 * BEFORE mounting, so the first guard sees the real auth state instead of
 * bouncing a logged-in user to /login on every refresh.
 *
 * registerSW() wires up the PWA service worker (registerType:'prompt' in
 * vite.config.ts, see its header comment — a waiting SW never force-reloads
 * a tab out from under an in-progress screen). onNeedRefresh flips a
 * reactive flag (pwa-update.ts) that UpdateBanner.vue reads — the ONLY way
 * the new SW is ever applied is an explicit tap on that banner's button,
 * never automatically. Without this handler, a waiting SW had no way to
 * reach the user at all (2026-07-21 — a shipped fix went unseen because
 * the old SW just kept serving the previous build indefinitely).
 * onRegisteredSW schedules PERIODIC update checks (pwa-update.ts's
 * scheduleUpdateChecks) — without it, `immediate: true` only checks for a
 * new SW once, when this module executes, which an already-installed PWA
 * just resumed from the home screen may never do again (2026-09-19 — a
 * device stayed on a build from before that day's microphone-permission
 * `public/_headers` fix, for days, because nothing ever re-checked).
 * virtual:pwa-register is a no-op import during `vite dev` unless
 * devOptions.enabled is set — fine for this scaffold.
 */
import { createApp } from 'vue';
import { registerSW } from 'virtual:pwa-register';
import App from './App.vue';
import { router } from './router/index.js';
import { hydrateSession, startSessionKeepAlive } from './lib/session.js';
import { startDictationSync } from './lib/dictation-queue.js';
import { startFormSync } from './lib/form-outbox.js';
import { startDocumentPrefetch } from './lib/offline-documents.js';
import { setUnauthorizedHandler } from './lib/http.js';
import { handleSessionLost } from './lib/session-lost.js';
import { needRefresh, scheduleUpdateChecks, setUpdateHandler } from './lib/pwa-update.js';
import { applyAppearance } from './lib/appearance.js';
import './style.css';

applyAppearance();

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    needRefresh.value = true;
  },
  onRegisteredSW(_swScriptUrl, registration) {
    if (registration) scheduleUpdateChecks(registration);
  },
});
setUpdateHandler(updateSW);

// A 401 mid-use (session expired, or signed out in the web panel, which
// shares the API cookie) goes to the login instead of leaving every screen
// failing (fix/operator-session-lost).
setUnauthorizedHandler(() => {
  void handleSessionLost(router);
});

void hydrateSession().finally(() => {
  createApp(App).use(router).mount('#app');
  // Dictations recorded with no signal go out on their own (operator-dictation-queue).
  startDictationSync();
  // So do field forms saved with no signal (operator-offline-forms).
  startFormSync();
  // Documents are downloaded ahead, so a permit opens at the gate with no
  // signal even if it was never opened (operator-document-prefetch).
  startDocumentPrefetch();
  // Keep the session (and its expiry) fresh while the app is used (fix/sliding-session).
  startSessionKeepAlive(() => {
    void handleSessionLost(router);
  });
});
